import { useQueryClient } from "@tanstack/react-query";
import { Fragment, useContext, useEffect, useState } from "react";
import dayjs from "dayjs";

import MyTitle from "../../../../components/MyTitle";
import MyLoading from "../../../../components/MyLoading";
import DeleteBoardItemCommentDialog from "../../../Common/DeleteBoardItemCommentDialog";
import FileItem from "../../../../components/inputs/MyInputFile/FileItem";
import MyButton from "../../../../components/MyButton";

import { AuthContext } from "../../../../context/AuthProvider";
import { PopupContext } from "../../../../context/PopupProvider";
import { useBoardItem } from "../../../../hooks/useBoardItem";
import { useBoardItemComment } from "../../../../hooks/useBoardItemComment";

const ResearchCommentList03 = ({
  board,
  boardItem,
  selectComment,
  setSelectComment,
  setReplyMode,
}) => {
  const { useGetBoardItemComments } = useBoardItemComment();
  const { updateBoardItemMutation } = useBoardItem();
  const { openPopup } = useContext(PopupContext);
  const queryClient = useQueryClient();
  const { isAdmin } = useContext(AuthContext);

  const { data, isLoading } = useGetBoardItemComments(
    {
      size: 10000,
      page: 1,
      sort: "createdDate,Asc",
      boardCode: board?.boardCode,
      itemNo: boardItem?.itemNo,
      useComment: board?.options?.useComment,
    },
    (data) => data.data
  );

  const [rows, setRows] = useState(null);

  const openPopupDelete = (commentNo) =>
    openPopup(
      <DeleteBoardItemCommentDialog
        mode="reply"
        boardCode={board?.boardCode}
        itemNo={boardItem?.itemNo}
        commentNo={commentNo}
        deleteMessage="답변이 삭제되었습니다."
        onRedirect={() => {
          updateBoardItemMutation.mutate(
            {
              boardCode: board?.boardCode,
              itemNo: boardItem?.itemNo,
              payload: {
                title: boardItem.title,
                body: boardItem.body,
                additionalItems: {
                  status: "reviewing",
                },
                attachedFiles: boardItem.attachedFiles,
                userId: boardItem.writer.userId,
              },
            },
            {
              onSuccess: (res) => {
                if (res) {
                  queryClient.invalidateQueries({
                    queryKey: ["/board-item/{boardCode}/{itemNo}"],
                  });
                  setRows(null);
                  setSelectComment(null);
                  setReplyMode(false);
                }
              },
            }
          );
        }}
      />
    );

  const onSelectComment = (item) => {
    const result = { ...item };
    result.hidden = true;

    setRows(null);
    setSelectComment(result);
  };

  useEffect(() => {
    if (data?.content?.length && !isLoading && !selectComment) {
      setRows([...data.content.map((c) => ({ ...c, hidden: false }))]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, data, selectComment]);

  return isLoading ? (
    <MyLoading className="h-[150px]" />
  ) : !!rows?.length ? (
    <div className="overflow-x-auto max-w-full">
      <MyTitle title="답변 내용" className="mb-4" />
      {rows.map((item) => (
        <Fragment key={item.commentNo}>
          <table className="w-full border-collapse mb-3 border-t-2 pt-10 border-primary">
            <tbody>
              <tr className="md:table-row hidden">
                <th className="text-left p-4 font-semibold bg-gray-100 border border-gray-border 2xl:text-base text-sm md:w-[25%]">
                  작성자
                </th>
                <td className="text-left p-4 border border-gray-border text-gray-dark 2xl:text-base text-sm w-[25%]">
                  {item?.writer?.name}
                </td>
                <th className="text-left p-4 font-semibold bg-gray-100 border border-gray-border 2xl:text-base text-sm w-[25%]">
                  작성일
                </th>
                <td className="text-left p-4 border border-gray-border text-gray-dark 2xl:text-base text-sm w-[25%]">
                  {dayjs(item?.createdDate).format("YYYY-MM-DD")}
                </td>
              </tr>
              <tr className="md:hidden">
                <th className="text-left p-4 font-semibold bg-gray-100 border border-gray-border 2xl:text-base text-sm w-[100px]">
                  작성자
                </th>
                <td className="text-left p-4 border border-gray-border text-gray-dark 2xl:text-base text-sm w-auto">
                  {item?.writer?.name}
                </td>
              </tr>
              <tr className="md:hidden">
                <th className="text-left p-4 font-semibold bg-gray-100 border border-gray-border 2xl:text-base text-sm w-[100px]">
                  작성일
                </th>
                <td className="text-left p-4 border border-gray-border text-gray-dark 2xl:text-base text-sm w-auto">
                  {dayjs(item?.createdDate).format("YYYY-MM-DD")}
                </td>
              </tr>
              <tr>
                <th className="text-left p-4 font-semibold bg-gray-100 border border-gray-border 2xl:text-base text-sm md:w-[25%] w-[100px]">
                  답변내용
                </th>
                <td
                  className="text-left p-4 border border-gray-border text-gray-dark 2xl:text-base text-sm w-auto"
                  colSpan={3}
                >
                  {item?.comment ?? "-"}
                </td>
              </tr>
              <tr>
                <th className="text-left p-4 font-semibold bg-gray-100 border border-gray-border 2xl:text-base text-sm md:w-[25%] w-[100px]">
                  첨부파일
                </th>
                <td
                  className="text-left p-4 border border-gray-border text-gray-dark 2xl:text-base text-sm w-auto"
                  colSpan={3}
                >
                  {item?.attachedFiles?.length > 0 ? (
                    <>
                      {item.attachedFiles.map((c) => (
                        <FileItem key={c.fileId} file={c} />
                      ))}
                    </>
                  ) : (
                    "-"
                  )}
                </td>
              </tr>
            </tbody>
          </table>

          {isAdmin && (
            <div className="flex items-center justify-end sm:gap-4 gap-2 mb-8">
              <MyButton
                variant="outlined"
                className="sm:min-w-[120px]"
                onClick={() => openPopupDelete(item.commentNo)}
              >
                <span className="font-semibold">삭제</span>
              </MyButton>
              <MyButton
                variant="contained"
                color="primary"
                className="sm:min-w-[120px]"
                onClick={() => onSelectComment(item)}
              >
                답변 수정하기
              </MyButton>
            </div>
          )}
        </Fragment>
      ))}
    </div>
  ) : null;
};

export default ResearchCommentList03;
